// Instellingen voor elk scherm: één keer laden (App.tsx) en de regels in planning.ts zetten, zodat deadline,
// categoryOf en catOf de ingestelde drempels en labelbetekenis gebruiken zonder dat hun aanroepen veranderen.
// Andere instellingen (rechten, capaciteit) leest een scherm met useSettings().
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { settingsApi, type Settings, type SettingsResponse } from "./api-settings";
import { getRules, setRules } from "./planning";

/** Zet de regels; true als ze veranderd zijn. */
function applySettings(s: Settings): boolean {
  const before = JSON.stringify(getRules());
  setRules({ soonDays: s.soonDays, lateDays: s.lateDays, labels: s.labels });
  return JSON.stringify(getRules()) !== before;
}

/** Categorie en deadlinestatus zijn afgeleid: schermen die ze per planningsdata onthouden, opnieuw laten rekenen. */
function recompute(qc: QueryClient) {
  const at = qc.getQueryState(["planning"])?.dataUpdatedAt;
  qc.setQueryData(["planning"], (d: unknown) => (d && typeof d === "object" ? { ...d } : d), { updatedAt: at });
}

export function useSettings() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["settings"],
    // de regels staan goed vóór een scherm de nieuwe waarden te zien krijgt
    queryFn: async () => {
      const r = await settingsApi.get();
      if (applySettings(r.settings)) recompute(qc);
      return r;
    },
    staleTime: 30e3,
    refetchInterval: 60e3, // een wijziging geldt binnen een minuut voor iedereen
  });
}

/** Na het bewaren: regels en gedeelde gegevens meteen bijwerken, zodat alle schermen de nieuwe waarden tonen. */
export function useApplySaved() {
  const qc = useQueryClient();
  return (r: SettingsResponse) => {
    const changed = applySettings(r.settings);
    qc.setQueryData<SettingsResponse>(["settings"], (old) => ({ ...old, ...r }));
    qc.invalidateQueries({ queryKey: ["audit"] });
    if (changed) recompute(qc);
  };
}

/** Wat een medewerker mag (Instellingen, Rollen en rechten); de server controleert hetzelfde in rules.refusal. */
export function rightsOf(s: Settings | undefined) {
  return {
    deadline: s?.deadlineRight ?? "voorstel",
    ownTasks: s?.ownTasks ?? true,
    pickUnowned: s?.pickUnowned ?? true,
    finishOthers: s?.finishOthers ?? false,
    seeBoard: s?.seeBoard ?? true,
    seeCapacity: s?.seeCapacity ?? true,
  };
}
